import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { AccountType, IReclassesAccounts } from '@modeso/types__aumico-lib-annualreport-be';
import { v4 as uuidv4 } from 'uuid';
import { DISPLAY_TYPES } from '../../../constants/adjustments-and-reclasses/displayTypes.constant';
import { EXTRA_TILE_CLASSES } from '../../../constants/adjustments-and-reclasses/extraTileClasses.constant';
import { STYLE_CLASSES } from '../../../constants/adjustments-and-reclasses/styleClasses.constant';
import { TILE_CLASSES } from '../../../constants/adjustments-and-reclasses/tileClasses.constant';
import { localizationKeys } from '../../../constants/localizationKeys';
import { CellId } from '../../../enums/adjustments-and-reclasses/CellId.enum';
import { MappingDisplayItemTypes } from '../../../enums/adjustments-and-reclasses/MappingDisplayItemTypes.enum';
import { CalculableMappingItems } from '../../../types/adjustments-and-reclasses/CalculableMappingItems.type';
import { CellDisplayType } from '../../../types/adjustments-and-reclasses/CellDisplayType.type';
import { CreateReclassesHandler } from '../../../types/adjustments-and-reclasses/CreateReclassesHandler.type';
import { MappingDisplayItem } from '../../../types/adjustments-and-reclasses/MappingDisplayItem.type';
import { MappingDisplayItemWithForm } from '../../../types/adjustments-and-reclasses/MappingDisplayItemWithForm.type';
import { NumericCell } from '../../../types/adjustments-and-reclasses/NumericCell.type';
import { RemoveReclassesHandler } from '../../../types/adjustments-and-reclasses/RemoveReclassesHandler.type';

export class AdjustmentAndReclassesUIHelper {
    /**
     * REV-XXXXX: when true, the reclass cells (`OPEN_RECLASSE_VALUE`
     * and `CLOSE_RECLASSE_VALUE`) render in non-editable state. Set by
     * the group-form component from its `readonlyReclasses` @Input,
     * which the consuming page derives from `isReclassNewWorld(ar)`.
     * Adjustment cells, balance cells, and every other cell type remain
     * editable as before.
     */
    public readonlyReclasses = false;

    public updateCalculations(displayItem: MappingDisplayItem, mappingItem: CalculableMappingItems): void {
        let closingReClass: number | undefined;
        if (mappingItem.closingReClass != null && NumberUtils.isFinite(mappingItem.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(mappingItem.closingReClass);
        }

        let openingReClass: number | undefined;
        if (mappingItem.openingReClass != null && NumberUtils.isFinite(mappingItem.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(mappingItem.openingReClass);
        }

        let closingAdjustment: number | undefined;
        if (mappingItem.closingAdjustment != null && NumberUtils.isFinite(mappingItem.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(mappingItem.closingAdjustment);
        }

        let openingAdjustment: number | undefined;
        if (mappingItem.openingAdjustment != null && NumberUtils.isFinite(mappingItem.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(mappingItem.openingAdjustment);
        }

        let closingValue = 0;
        if (
            mappingItem.calculatedClosingBalance != null &&
            NumberUtils.isFinite(mappingItem.calculatedClosingBalance)
        ) {
            closingValue = NumberUtils.roundNumber(mappingItem.calculatedClosingBalance);
        }

        let openingValue = 0;
        if (
            mappingItem.calculatedOpeningBalance != null &&
            NumberUtils.isFinite(mappingItem.calculatedOpeningBalance)
        ) {
            openingValue = NumberUtils.roundNumber(mappingItem.calculatedOpeningBalance);
        }
        displayItem.closingReClass = closingReClass;
        displayItem.openingReClass = openingReClass;
        displayItem.closingAdjustment = closingAdjustment;
        displayItem.openingAdjustment = openingAdjustment;
        displayItem.closing = closingValue;
        displayItem.opening = openingValue;
        this.updateCells(displayItem, mappingItem);
    }

    private updateCells(displayItem: MappingDisplayItem, mappingItem: CalculableMappingItems): void {
        for (const cell of displayItem.cells) {
            if (cell.type === DISPLAY_TYPES.numeric) {
                this.updateNumericCells(cell, mappingItem);
            }
        }
    }

    private updateNumericCells(cell: NumericCell, mappingItem: CalculableMappingItems): void {
        switch (cell.id) {
            case CellId.CLOSE_VALUE:
                let closingValue = 0;
                if (
                    mappingItem.calculatedClosingBalance != null &&
                    NumberUtils.isFinite(mappingItem.calculatedClosingBalance)
                ) {
                    closingValue = NumberUtils.roundNumber(mappingItem.calculatedClosingBalance);
                }
                cell.value = closingValue;
                break;
            case CellId.OPEN_VALUE:
                let openingValue = 0;
                if (
                    mappingItem.calculatedOpeningBalance != null &&
                    NumberUtils.isFinite(mappingItem.calculatedOpeningBalance)
                ) {
                    openingValue = NumberUtils.roundNumber(mappingItem.calculatedOpeningBalance);
                }
                cell.value = openingValue;
                break;
            case CellId.CLOSE_ADJUSTEMENT_VALUE:
                let closingAdjustment: number | null = null;
                if (mappingItem.closingAdjustment != null && NumberUtils.isFinite(mappingItem.closingAdjustment)) {
                    closingAdjustment = NumberUtils.roundNumber(mappingItem.closingAdjustment);
                }
                cell.value = closingAdjustment;
                break;
            case CellId.OPEN_ADJUSTEMENT_VALUE:
                let openingAdjustment: number | null = null;
                if (mappingItem.openingAdjustment != null && NumberUtils.isFinite(mappingItem.openingAdjustment)) {
                    openingAdjustment = NumberUtils.roundNumber(mappingItem.openingAdjustment);
                }
                cell.value = openingAdjustment;
                break;
            case CellId.CLOSE_RECLASSE_VALUE:
                let closingReClass: number | null = null;
                if (mappingItem.closingReClass != null && NumberUtils.isFinite(mappingItem.closingReClass)) {
                    closingReClass = NumberUtils.roundNumber(mappingItem.closingReClass);
                }
                cell.value = closingReClass;
                break;
            case CellId.OPEN_RECLASSE_VALUE:
                let openingReClass: number | null = null;
                if (mappingItem.openingReClass != null && NumberUtils.isFinite(mappingItem.openingReClass)) {
                    openingReClass = NumberUtils.roundNumber(mappingItem.openingReClass);
                }
                cell.value = openingReClass;
                break;
            case CellId.CLOSE_INTERNAL_VALUE:
                cell.value = this.calculateInternalTotal(
                    mappingItem.calculatedClosingBalance,
                    mappingItem.closingReClass,
                    mappingItem.closingAdjustment,
                );
                break;
            case CellId.OPEN_INTERNAL_VALUE:
                cell.value = this.calculateInternalTotal(
                    mappingItem.calculatedOpeningBalance,
                    mappingItem.openingReClass,
                    mappingItem.openingAdjustment,
                );
                break;
            case CellId.CHANGE_IN_ADJUSTMENTS:
                cell.value = this.calculateChangeInAdjustment(
                    mappingItem.closingAdjustment,
                    mappingItem.openingAdjustment,
                );
                break;

            default:
                throw Error(`This cell id: ${cell.id} is not supported`);
        }
    }

    private getNewReclassesAccount: () => IReclassesAccounts = () => ({
        accountNumber: uuidv4(),
        type: AccountType.RECLASSES,
        name: '',
        openingReClass: undefined,
        closingReClass: undefined,
        closingAdjustment: undefined,
        openingAdjustment: undefined,
        calculatedClosingBalance: 0,
        calculatedOpeningBalance: 0,
    });
    readonly createReclassesCreationHandler = (
        item: { reclassesAccounts: IReclassesAccounts[] },
        doAfter: () => void,
    ): CreateReclassesHandler => {
        const createReclassesAfter = () => {
            item.reclassesAccounts.push(this.getNewReclassesAccount());
            doAfter();
        };
        return createReclassesAfter;
    };

    readonly createReclassesRemoveHandlers = (
        item: { reclassesAccounts: IReclassesAccounts[] },
        removeIndex: number | null,
        doAfter: (id: string) => void,
    ): RemoveReclassesHandler => {
        const removeReclasses = () => {
            if (removeIndex != null) {
                const numberOfItemsToRemoveFromArray = 1;
                const reclassesAccountId = item.reclassesAccounts[removeIndex].accountNumber;
                item.reclassesAccounts.splice(removeIndex, numberOfItemsToRemoveFromArray);
                doAfter(reclassesAccountId);
            }
        };

        return removeReclasses;
    };

    readonly getHeadingNode = ({
        title,
        dateEnd,
        dateStart,
        showChange,
        showIncome,
    }: {
        title: string;
        dateEnd: string;
        dateStart: string;
        showChange: boolean;
        showIncome: boolean;
    }) => {
        const cells: CellDisplayType[] = [
            { value: title, class: STYLE_CLASSES.titleGeneral },

            { value: dateEnd, class: STYLE_CLASSES.closingCurrent, type: DISPLAY_TYPES.date },
            {
                value: localizationKeys.aumico_templates_reclass_label_reclass,
                class: STYLE_CLASSES.closingReclass,
                type: DISPLAY_TYPES.translate,
            },
            {
                value: localizationKeys.aumico_templates_reclass_label_adjustment,
                class: STYLE_CLASSES.closingAdjustment,
                type: DISPLAY_TYPES.translate,
            },
            {
                value: localizationKeys.aumico_templates_reclass_label_internal,
                class: STYLE_CLASSES.closingInternal,
                type: DISPLAY_TYPES.translate,
            },

            { value: dateStart, class: STYLE_CLASSES.openingCurrent, type: DISPLAY_TYPES.date },
            {
                value: localizationKeys.aumico_templates_reclass_label_reclass,
                class: STYLE_CLASSES.openingReclass,
                type: DISPLAY_TYPES.translate,
            },
            {
                value: localizationKeys.aumico_templates_reclass_label_adjustment,
                class: STYLE_CLASSES.openingAdjustment,
                type: DISPLAY_TYPES.translate,
            },
            {
                value: localizationKeys.aumico_templates_reclass_label_internal,
                class: STYLE_CLASSES.openingInternal,
                type: DISPLAY_TYPES.translate,
            },

            ...this.castArr(
                showChange
                    ? [
                          {
                              value: localizationKeys.aumico_templates_reclass_label_change_in_adjustment,
                              class: STYLE_CLASSES.changeInAdjustment,
                              type: DISPLAY_TYPES.translate,
                          },
                      ]
                    : [],
            ),
            ...this.castArr(
                showIncome
                    ? [
                          {
                              value: localizationKeys.aumico_templates_reclass_label_income,
                              class: STYLE_CLASSES.incomeAccount,
                              type: DISPLAY_TYPES.translate,
                          },
                      ]
                    : [],
            ),
        ];
        return {
            class: TILE_CLASSES.headingTile,
            cells,
        };
    };
    readonly castToItemWithForm = (displayItem: MappingDisplayItem) => displayItem as MappingDisplayItemWithForm;
    readonly castArr = (array: CellDisplayType[]) => array as CellDisplayType[];
    private getCellsDisplay = (
        item: MappingDisplayItem,
        showChange: boolean,
        showIncome: boolean,
    ): CellDisplayType[] => {
        const numeric = { type: DISPLAY_TYPES.numeric } as const;

        // cast IMappingDisplayItem
        const isAccount = item.isReclasses || item.isAccount;
        const canExpand = item.hasChild;

        const cells: CellDisplayType[] = [
            // title feilds
            ...this.castArr(
                canExpand
                    ? [
                          {
                              class: STYLE_CLASSES.expandAction,
                              type: DISPLAY_TYPES.expandAction,
                              editable: true,
                          },
                      ]
                    : [],
            ),
            ...this.getTitleFields(isAccount, item),

            // closing fields
            ...this.getClosingFields(numeric, item, isAccount),
            // opening fields
            ...this.getOpeningFields(numeric, item, isAccount),

            ...this.getBalanceRelatedFields({
                showChange,
                numeric,
                item,
                showIncome,
                isAccount,
            }),
        ];
        return cells;
    };
    readonly prepareForDisplay = (
        node: MappingDisplayItem,
        showChange: boolean,
        showIncome: boolean,
    ): MappingDisplayItem => {
        node.isFLI = MappingDisplayItemTypes.FLI === node.type;
        node.isSubFLI = MappingDisplayItemTypes.SUB_FLI === node.type;

        node.isGroup = MappingDisplayItemTypes.GROUP_TOTAL === node.type;
        node.isSubtotal = MappingDisplayItemTypes.SUBTOTAL === node.type;
        node.isAccount = MappingDisplayItemTypes.ACCOUNT === node.type;
        node.isReclasses = MappingDisplayItemTypes.RECLASSES === node.type;
        node.hasChild = node.isFLI || node.isSubFLI || node.isGroup;

        node.class =
            (node.level === 0 && node.isGroup
                ? EXTRA_TILE_CLASSES.rowHeightBig + ' '
                : EXTRA_TILE_CLASSES.rowHeightSmall + ' ') +
            (node.isGroup
                ? TILE_CLASSES.groupTile
                : node.isFLI
                ? TILE_CLASSES.fliTile
                : node.isSubFLI
                ? TILE_CLASSES.subFliTile
                : node.isReclasses
                ? TILE_CLASSES.adjustmentTile
                : node.isAccount
                ? TILE_CLASSES.accountTile
                : node.isSubtotal
                ? TILE_CLASSES.subtotalTile
                : '');
        node.cells = this.getCellsDisplay(node, showChange, showIncome);

        return node;
    };
    private getBalanceRelatedFields({
        showChange,
        numeric,
        item,
        showIncome,
        isAccount,
    }: {
        showChange: boolean;
        numeric: { readonly type: 'numeric' };
        item: MappingDisplayItem;
        showIncome: boolean;
        isAccount: boolean;
    }): CellDisplayType[] {
        const that = this;
        return [
            ...this.castArr(
                showChange
                    ? [
                          {
                              ...numeric,
                              class: STYLE_CLASSES.changeInAdjustment,

                              get value() {
                                  return that.calculateChangeInAdjustment(
                                      item.closingAdjustment,
                                      item.openingAdjustment,
                                  );
                              },
                              control: null,
                              id: CellId.CHANGE_IN_ADJUSTMENTS,
                          },
                      ]
                    : [],
            ),
            ...this.castArr(
                showIncome
                    ? [
                          {
                              editable: isAccount,
                              type: DISPLAY_TYPES.incomeAccount,
                              class: STYLE_CLASSES.incomeAccount,
                          },
                      ]
                    : [],
            ),
        ];
    }

    private getOpeningFields(
        numeric: { readonly type: 'numeric' },
        item: MappingDisplayItem,
        isAccount: boolean,
    ): CellDisplayType[] {
        const that = this;
        let openingReClass: number | undefined;
        if (item.openingReClass != null && NumberUtils.isFinite(item.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(item.openingReClass);
        }

        let openingAdjustment: number | undefined;
        if (item.openingAdjustment != null && NumberUtils.isFinite(item.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(item.openingAdjustment);
        }
        return [
            {
                ...numeric,
                class: STYLE_CLASSES.openingCurrent,
                get value() {
                    return NumberUtils.roundNumber(item.opening);
                },
                control: null,
                id: CellId.OPEN_VALUE,
            },
            {
                ...numeric,
                // REV-XXXXX: reclass column is locked for reports created
                // after the new-reclasses cutover. The wider cell stays an
                // account-level cell (`isAccount`) so other reclass-related
                // styling still applies; only `editable` is gated.
                editable: isAccount && !this.readonlyReclasses,
                class: STYLE_CLASSES.openingReclass,
                get value() {
                    return openingReClass;
                },
                control: this.castToItemWithForm(item).openingReClassControl,
                id: CellId.OPEN_RECLASSE_VALUE,
            },
            {
                ...numeric,
                editable: isAccount,
                class: STYLE_CLASSES.openingAdjustment,
                get value() {
                    return openingAdjustment;
                },
                control: this.castToItemWithForm(item).openingAdjustmentControl,
                id: CellId.OPEN_ADJUSTEMENT_VALUE,
            },
            {
                ...numeric,
                class: STYLE_CLASSES.openingInternal,
                get value() {
                    return that.calculateInternalTotal(item.opening, item.openingReClass, item.openingAdjustment);
                },
                control: null,
                id: CellId.OPEN_INTERNAL_VALUE,
            },
        ];
    }

    private getClosingFields(
        numeric: { readonly type: 'numeric' },
        item: MappingDisplayItem,
        isAccount: boolean,
    ): CellDisplayType[] {
        const that = this;
        let closingReClass: number | undefined;
        if (item.closingReClass != null && NumberUtils.isFinite(item.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(item.closingReClass);
        }

        let closingAdjustment: number | undefined;
        if (item.closingAdjustment != null && NumberUtils.isFinite(item.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(item.closingAdjustment);
        }
        return [
            {
                ...numeric,
                class: STYLE_CLASSES.closingCurrent,
                get value() {
                    return NumberUtils.roundNumber(item.closing);
                },
                control: null,
                id: CellId.CLOSE_VALUE,
            },
            {
                ...numeric,
                // REV-XXXXX: see note on OPEN_RECLASSE_VALUE — reclass
                // column locks for post-cutover reports.
                editable: isAccount && !this.readonlyReclasses,
                class: STYLE_CLASSES.closingReclass,
                get value() {
                    return closingReClass;
                },
                control: this.castToItemWithForm(item).closingReClassControl,
                id: CellId.CLOSE_RECLASSE_VALUE,
            },
            {
                ...numeric,
                editable: isAccount,
                class: STYLE_CLASSES.closingAdjustment,
                get value() {
                    return closingAdjustment;
                },
                control: that.castToItemWithForm(item).closingAdjustmentControl,
                id: CellId.CLOSE_ADJUSTEMENT_VALUE,
            },
            {
                ...numeric,
                class: STYLE_CLASSES.closingInternal,
                get value() {
                    return that.calculateInternalTotal(item.closing, item.closingReClass, item.closingAdjustment);
                },
                control: null,
                id: CellId.CLOSE_INTERNAL_VALUE,
            },
        ];
    }

    private calculateInternalTotal(
        value: number | undefined,
        reclass: number | undefined,
        adjustment: number | undefined,
    ): number {
        return NumberUtils.sumIgnoringInfiniteNumbers([value, reclass, adjustment], 0);
    }

    public calculateChangeInAdjustment(
        closingAdjustment: number | undefined,
        openingAdjustment: number | undefined,
    ): number | null {
        if (
            closingAdjustment != null &&
            !NumberUtils.isFinite(closingAdjustment) &&
            openingAdjustment != null &&
            !NumberUtils.isFinite(openingAdjustment)
        ) {
            return null;
        }
        return NumberUtils.subtractNumbers(closingAdjustment ?? 0, openingAdjustment ?? 0);
    }

    private getTitleFields(isAccount: boolean, item: MappingDisplayItem): CellDisplayType[] {
        return this.castArr(
            isAccount
                ? [
                      {
                          class: STYLE_CLASSES.titleAccountNumber,
                          get value() {
                              return item.accountNumber;
                          },
                          editable: item.isReclasses,
                      },
                      {
                          class: STYLE_CLASSES.titleAccountName,
                          get value() {
                              return item.title;
                          },
                          control: this.castToItemWithForm(item).nameControl,
                          editable: item.isReclasses,
                      },
                      {
                          class: STYLE_CLASSES.action,
                          type: DISPLAY_TYPES.action,
                          extraClass: item.isReclasses
                              ? EXTRA_TILE_CLASSES.removeCircleIcon
                              : EXTRA_TILE_CLASSES.resetIcon,
                          action: ($event) =>
                              this.clickUtil(
                                  $event,
                                  item.type === MappingDisplayItemTypes.RECLASSES
                                      ? item.removeReclassesAccount
                                      : () => {},
                              ),
                          editable: true,
                      },
                  ]
                : item.isSubtotal
                ? [
                      {
                          class: STYLE_CLASSES.titleSubtotal,
                          get value() {
                              return item.title;
                          },
                          control: null,
                      },
                  ]
                : [
                      {
                          class: item.isFLI || item.isSubFLI ? STYLE_CLASSES.titleFli : STYLE_CLASSES.title,
                          get value() {
                              return item.title;
                          },
                          control: null,
                      },
                      ...this.castArr(
                          item.isFLI || item.isSubFLI
                              ? [
                                    {
                                        class: STYLE_CLASSES.action,
                                        type: DISPLAY_TYPES.action,
                                        extraClass: EXTRA_TILE_CLASSES.plusIcon,
                                        action: ($event) =>
                                            this.clickUtil(
                                                $event,
                                                item.type === MappingDisplayItemTypes.FLI ||
                                                    item.type === MappingDisplayItemTypes.SUB_FLI
                                                    ? item.createReclassesAccount
                                                    : () => {},
                                            ),
                                        editable: true,
                                    },
                                ]
                              : [],
                      ),
                  ],
        );
    }

    /**
     * It will stop click event propagation then run doAfter
     * @param $event the click event
     * @param doAfter do after callback
     */
    public clickUtil($event: Event, doAfter: () => void) {
        $event.stopPropagation();
        doAfter();
    }
}
